import { useNavigate } from "react-router-dom";
import { ArrowRight, CheckCircle2, Clock, PlayCircle } from "lucide-react";
import { LearnArticle } from "@/types/learn";
import { cn } from "@/lib/utils";

interface ArticleCardProps {
  article: LearnArticle;
}

const ArticleCard = ({ article }: ArticleCardProps) => {
  const navigate = useNavigate();
  const isComplete = !!article.completed_at;

  return (
    <button
      onClick={() => navigate(`/learn/articles/${article.slug}`)}
      className={cn(
        "group relative flex flex-col text-left p-4 rounded-xl bg-white border transition-all duration-200 h-full",
        isComplete
          ? "border-genie-primary/40"
          : "border-gray-200 hover:border-genie-primary/40 hover:shadow-md",
      )}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-gradient-to-r from-genie-primary/10 to-genie-secondary/10 text-genie-primary capitalize">
          {article.category.replace(/-/g, " ")}
        </span>
        {isComplete && (
          <CheckCircle2 className="h-4 w-4 text-genie-primary flex-shrink-0" aria-label="Completed" />
        )}
      </div>

      <h3 className="text-sm font-semibold text-genie-text mb-1 line-clamp-2">{article.title}</h3>

      {article.summary && (
        <p className="text-xs text-gray-600 line-clamp-2 mb-3 flex-1">{article.summary}</p>
      )}

      <div className="flex items-center justify-between gap-3 mt-auto pt-2 border-t border-gray-100">
        <div className="flex items-center gap-3 text-[11px] text-gray-500">
          <span className="inline-flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {article.est_minutes} min
          </span>
          {article.video_url && (
            <span className="inline-flex items-center gap-1 text-genie-secondary">
              <PlayCircle className="h-3 w-3" />
              Video
            </span>
          )}
        </div>
        <ArrowRight className="h-3.5 w-3.5 text-gray-300 group-hover:text-genie-primary transition-colors" />
      </div>
    </button>
  );
};

export default ArticleCard;
